import { useState } from 'react';
import { Check, ChevronDown, AlertTriangle } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { PlatformIcon, InfoTip } from '@/shared/ui';
import type { Platform } from '@/shared/ui';
import type { FilterState } from './engine';
import { PLATFORMS_META, catLabel, catSubtopics } from './engine';

type Props = {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  platformCounts: Record<string, number>;
  nicheCounts: Record<string, number>;
  availableNiches: string[];
  multiPlatformWarning: boolean;
  onReset: () => void;
};

export function FilterSidebar({ filters, onChange, platformCounts, nicheCounts, availableNiches, multiPlatformWarning, onReset }: Props) {
  const set = <K extends keyof FilterState>(key: K, val: FilterState[K]) => onChange({ ...filters, [key]: val });
  const tog = <T extends string>(arr: T[], val: T): T[] =>
    arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  return (
    <div className="bg-white rounded-lg border border-ink-12 flex flex-col overflow-hidden h-full">
      {/* Search */}
      <div className="p-3 border-b border-ink-12 shrink-0">
        <input
          placeholder="Поиск по названию, категории…"
          value={filters.search}
          onChange={e => set('search', e.target.value)}
          className="w-full h-8 px-3 rounded-md border border-ink-12 text-[13px] bg-cream outline-none focus:border-ink"
        />
      </div>

      <div className="overflow-y-auto flex-1 min-h-0 px-3 divide-y divide-ink-12/60">

        {/* Площадка */}
        <FilterGroup label="Площадка" defaultOpen>
          {PLATFORMS_META.map(p => {
            const cnt = platformCounts[p.id] ?? 0;
            const checked = filters.platform.includes(p.id);
            return (
              <MultiCheckItem key={p.id} label={p.label} count={cnt}
                checked={checked} disabled={cnt === 0 && !checked}
                icon={<PlatformIcon platform={p.id as Platform} size={13} mono={checked} />}
                onChange={() => set('platform', tog(filters.platform, p.id))} />
            );
          })}
        </FilterGroup>

        {/* Охват */}
        <FilterGroup label="Охват (просмотры)" defaultOpen>
          <RangeInput minVal={filters.reachMin} maxVal={filters.reachMax}
            onMin={v => set('reachMin', v)} onMax={v => set('reachMax', v)}
            presets={[
              { label: '<10K', min: null, max: 10000 },
              { label: '10–100K', min: 10000, max: 100000 },
              { label: '100K–1M', min: 100000, max: 1000000 },
              { label: '>1M', min: 1000000, max: null },
            ]} />
        </FilterGroup>

        {/* Цена */}
        <FilterGroup label="Цена размещения" defaultOpen>
          <RangeInput minVal={filters.priceMin} maxVal={filters.priceMax} unit="₽"
            onMin={v => set('priceMin', v)} onMax={v => set('priceMax', v)}
            presets={[
              { label: 'до 20К', min: null, max: 20000 },
              { label: '20–60К', min: 20000, max: 60000 },
              { label: '60–150К', min: 60000, max: 150000 },
              { label: '150К+', min: 150000, max: null },
            ]} />
          <ToggleRow label="Скрывать без цены" value={filters.hidePriceNull}
            onChange={v => set('hidePriceNull', v)} />
        </FilterGroup>

        {/* Бюджет */}
        <FilterGroup label="Бюджет" tip="Показывает каналы с ценой ≤ указанной суммы">
          <NumInput placeholder="Мой бюджет" unit="₽"
            value={filters.budgetFit} onChange={v => set('budgetFit', v)} />
        </FilterGroup>

        {/* CPV */}
        <FilterGroup label="CPV" tip="Стоимость 1 просмотра. Активен только при одной выбранной площадке.">
          {multiPlatformWarning && (
            <div className="flex items-start gap-1.5 px-2.5 py-2 rounded-md bg-amber-50 text-[11.5px] text-amber-800 border border-amber-200">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              CPV несопоставим между площадками — выберите одну.
            </div>
          )}
          <RangeInput minVal={filters.cpvMin} maxVal={filters.cpvMax} unit="₽"
            onMin={v => set('cpvMin', v)} onMax={v => set('cpvMax', v)}
            presets={[
              { label: 'до 0.3₽', min: null, max: 0.3 },
              { label: 'до 0.5₽', min: null, max: 0.5 },
              { label: 'до 1₽', min: null, max: 1 },
            ]} />
        </FilterGroup>

        {/* ER */}
        <FilterGroup label="Вовлечённость (ER)" tip="Engagement Rate. Выше 5% — очень хорошо.">
          <RangeInput minVal={filters.erMin} maxVal={filters.erMax} unit="%"
            onMin={v => set('erMin', v)} onMax={v => set('erMax', v)}
            presets={[
              { label: 'от 3%', min: 3, max: null },
              { label: 'от 5%', min: 5, max: null },
              { label: 'от 8%', min: 8, max: null },
            ]} />
        </FilterGroup>

        {/* Качество */}
        <FilterGroup label="Качество канала">
          <div className="flex gap-1.5 flex-wrap">
            {([null, 50, 70, 80, 90] as (number | null)[]).map(s => (
              <ChipBtn key={String(s)} active={filters.scoreMin === s}
                onClick={() => set('scoreMin', s)}>
                {s === null ? 'Любой' : `≥ ${s}`}
              </ChipBtn>
            ))}
          </div>
        </FilterGroup>

        {/* Категории */}
        {availableNiches.length > 0 && (
          <FilterGroup label="Категории (включить)">
            <div className="max-h-[210px] overflow-y-auto -mx-1 px-1 flex flex-col gap-0.5">
              {availableNiches.map(n => {
                const cnt = nicheCounts[n] ?? 0;
                const checked = filters.niche.includes(n);
                return (
                  <MultiCheckItem key={n} label={catLabel(n)} count={cnt} hint={catSubtopics(n).join(' · ')}
                    checked={checked} disabled={cnt === 0 && !checked}
                    onChange={() => set('niche', tog(filters.niche, n))} />
                );
              })}
            </div>
          </FilterGroup>
        )}

        {availableNiches.length > 0 && (
          <FilterGroup label="Категории (исключить)">
            <div className="max-h-[210px] overflow-y-auto -mx-1 px-1 flex flex-col gap-0.5">
              {availableNiches.map(n => (
                <MultiCheckItem key={n} label={catLabel(n)} hint={catSubtopics(n).join(' · ')}
                  checked={filters.excludeNiche.includes(n)}
                  onChange={() => set('excludeNiche', tog(filters.excludeNiche, n))} />
              ))}
            </div>
          </FilterGroup>
        )}

      </div>

      <div className="p-3 border-t border-ink-12 shrink-0">
        <button onClick={onReset}
          className="w-full h-8 rounded-md text-[12.5px] font-display font-semibold text-ink-40 hover:text-ink hover:bg-ink-6 transition-colors">
          Сбросить все фильтры
        </button>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FilterGroup({ label, children, defaultOpen = false, tip }: {
  label: string; children: React.ReactNode; defaultOpen?: boolean; tip?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-2.5 text-left">
        <span className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
          {label}
          {tip && <InfoTip align="left">{tip}</InfoTip>}
        </span>
        <ChevronDown size={13} className={cn('text-ink-40 transition-transform shrink-0', open && 'rotate-180')} />
      </button>
      {open && <div className="pb-3 flex flex-col gap-2">{children}</div>}
    </div>
  );
}

type Preset = { label: string; min: number | null; max: number | null };

function RangeInput({ minVal, maxVal, unit, onMin, onMax, presets }: {
  minVal: number | null; maxVal: number | null; unit?: string;
  onMin: (v: number | null) => void; onMax: (v: number | null) => void;
  presets?: Preset[];
}) {
  const isActive = (p: Preset) => minVal === p.min && maxVal === p.max;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input type="number" placeholder="От" value={minVal ?? ''}
            onChange={e => onMin(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full h-8 pl-2.5 pr-6 rounded-md border border-ink-12 text-[12px] bg-white outline-none focus:border-ink [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
          {unit && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-40 pointer-events-none">{unit}</span>}
        </div>
        <span className="text-[11px] text-ink-40 shrink-0">—</span>
        <div className="relative flex-1">
          <input type="number" placeholder="До" value={maxVal ?? ''}
            onChange={e => onMax(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full h-8 pl-2.5 pr-6 rounded-md border border-ink-12 text-[12px] bg-white outline-none focus:border-ink [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
          {unit && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-40 pointer-events-none">{unit}</span>}
        </div>
      </div>
      {presets && (
        <div className="flex gap-1 flex-wrap">
          {presets.map(p => (
            <button key={p.label} onClick={() => { onMin(isActive(p) ? null : p.min); onMax(isActive(p) ? null : p.max); }}
              className={cn('h-6 px-2 rounded-pill border text-[11px] font-display font-semibold transition-colors',
                isActive(p) ? 'bg-ink text-white border-ink' : 'bg-white text-ink border-ink-12 hover:border-ink/40')}>
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NumInput({ placeholder, unit, value, onChange }: {
  placeholder: string; unit?: string; value: number | null; onChange: (v: number | null) => void;
}) {
  return (
    <div className="relative">
      <input type="number" placeholder={placeholder} value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="w-full h-8 pl-3 pr-7 rounded-md border border-ink-12 text-[12px] bg-white outline-none focus:border-ink [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
      {unit && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-40 pointer-events-none">{unit}</span>}
    </div>
  );
}

function MultiCheckItem({ label, hint, count, checked, disabled = false, icon, onChange }: {
  label: string; hint?: string; count?: number; checked: boolean; disabled?: boolean;
  icon?: React.ReactNode; onChange: () => void;
}) {
  return (
    <button onClick={disabled ? undefined : onChange}
      className={cn('flex items-start justify-between gap-2 py-1 text-left w-full', disabled && 'opacity-35 cursor-not-allowed')}>
      <div className="flex items-start gap-2 min-w-0">
        <span className={cn('shrink-0 mt-0.5 w-4 h-4 rounded border flex items-center justify-center',
          checked ? 'bg-ink border-ink' : 'border-ink-12 bg-white')}>
          {checked && <Check size={10} className="text-white" strokeWidth={3} />}
        </span>
        {icon}
        <div className="min-w-0 flex flex-col">
          <span className="text-[12.5px] truncate">{label}</span>
          {hint && <span className="text-[10.5px] text-ink-40 truncate">{hint}</span>}
        </div>
      </div>
      {count !== undefined && <span className="text-[11px] font-mono text-ink-40 shrink-0 mt-0.5">{count}</span>}
    </button>
  );
}

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[12.5px] leading-tight">{label}</span>
      <button onClick={() => onChange(!value)}
        className={cn('relative w-9 h-5 rounded-full transition-colors shrink-0', value ? 'bg-ink' : 'bg-ink-12')}>
        <span className={cn('absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
          value ? 'translate-x-[18px]' : 'translate-x-0.5')} />
      </button>
    </div>
  );
}

function ChipBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={cn('h-7 px-2.5 rounded-pill border text-[12px] font-display font-semibold transition-colors',
        active ? 'bg-ink text-white border-ink' : 'bg-white text-ink border-ink-12 hover:border-ink/40')}>
      {children}
    </button>
  );
}
